import { z } from 'zod';

export const StyleFormSchema = z
  .object({
    updatedAt: z.string().optional(),
    loraEnabled: z.boolean(),
    imagePublicUrl: z.union([
      z.string().min(1, 'Style preview is required'),
      z.instanceof(File),
    ]),
    status: z.string().optional(),
    loraModelFilePublicUrl: z.string().optional(),
    name: z
      .string()
      .min(3, 'Style name must be at least 3 characters long')
      .regex(/^[a-zA-Z0-9 ]+$/, {
        message: 'Only letters and numbers are allowed',
      })
      .max(100, 'Style name cannot exceed 100 characters'),
    negativePrompts: z
      .string()
      .min(3, 'Negative prompt must be at least 3 characters long')
      .max(1000, 'Negative prompt cannot exceed 1000 characters'),
    gptPromptTemplate: z
      .string()
      .nonempty('This field is required')
      .max(5000, 'GPT prompt template cannot exceed 5000 characters')
      .refine(
        (value) =>
          value.includes('{user_prompt}') &&
          value.includes('{security_prompt}'),
        {
          message:
            'GPT prompt template must include both "{user_prompt}" and "{security_prompt}"',
        }
      ),
    userPromptTemplate: z
      .string()
      .nonempty('This field is required')
      .max(5000, 'User prompt template cannot exceed 5000 characters')
      .refine((value) => value.includes('{user_prompt}'), {
        message: 'User prompt template must include "{user_prompt}"',
      }),
    loraModelTriggerWord: z.string().max(50, 'Max 50 characters').optional(),
    loraModelStrength: z
      .preprocess(
        (val) => {
          const num = Number(val);
          return isNaN(num) ? undefined : num;
        },
        z
          .number({
            invalid_type_error: 'LoRA Strength must be a number',
          })
          .min(0, 'Enter a number from 0 to 2 (e.g. 0.3)')
          .max(2, 'Enter a number from 0 to 2 (e.g. 0.3)')
          .refine((val) => Number.isInteger(val * 10), {
            message: 'LoRA Strength must be in 0.1 increments',
          })
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.loraEnabled) return;

    const requiredTextFields = [
      'loraModelTriggerWord',
      'loraModelFilePublicUrl',
    ];

    for (const field of requiredTextFields) {
      const value = data[field];
      if (typeof value !== 'string' || value.trim() === '') {
        ctx.addIssue({
          path: [field],
          code: z.ZodIssueCode.custom,
          message: 'This field is required',
        });
      }
    }

    if (
      data.loraModelStrength === undefined ||
      data.loraModelStrength === null
    ) {
      ctx.addIssue({
        path: ['loraModelStrength'],
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
      });
    }
  });

export type StyleFormValues = z.infer<typeof StyleFormSchema>;
